
/* ============================== src/components/MainLayout.jsx ============================== */
// path: frontend/src/components/MainLayout.jsx
/**
 * Главный layout: навигация, топбар, роутер по хэшу, ModalRenderer.
 * RBAC-флаги для навигации — из useAuth().capabilities (серверные):
 *   - accessLibrary — вкладка «Шаблоны и черновики»;
 *   - accessReports — вкладка «Отчёты»;
 *   - accessJournal — вкладка «Журнал аудита»;
 *   - остальные view доступны всем аутентифицированным.
 *
 * Никаких hasRole/canExport/canAccessLibrary/canAccessView из
 * utils/permissions.js — только capabilities и локальная функция
 * canAccessView(viewId).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../hooks/useAuth";
import { useModals } from "../hooks/useModals";
import { useHashRoute } from "../hooks/useHashRoute";
import { useEmployeesQuery } from "../api/queries/employees";
import { useDepartmentsQuery } from "../api/queries/departments";
import { useMarkNotificationReadMutation } from "../api/queries/notifications";
import { useAssignmentApprovalsQuery } from "../api/queries/assignment-approvals";
import ModalRenderer from "./ModalRenderer";
import NotifBell from "./NotifBell";
import { ThemeToggle } from "./ThemeToggle";
import { SubstitutionBadge } from "./SubstitutionBadge";
import { ICONS } from "./iconsData";
import { Ic } from "./Icons";
import TasksView from "./views/TasksView";
import LibraryView from "./views/LibraryView";
import ProjectsView from "./views/ProjectsView";
import Gantt from "./Gantt";
import Calendar from "./Calendar";
import CabinetView from "./views/CabinetView";
import StaffView from "./views/StaffView";
import ReportsView from "./views/ReportsView";
import ArchiveView from "./views/ArchiveView";
import { RequestsView } from "./views/RequestsView";
import JournalView from "./views/JournalView";
import { ChangePasswordModal } from "./Modals/ChangePasswordModal";
import { initials } from "../utils/date";

import {
  ROUTE,
  VIEWS,
  DEFAULT_TAB,
  buildRoute,
  parseRoute,
} from "../utils/routes";

const REQUESTS_TAB_BY_NOTIF_TYPE = Object.freeze({
  delegation: "rd",
  registration: "reg",
  assignmentApproval: "assignments",
});

const readInitialView = () => {
  const r = parseRoute(window.location.hash);
  if (r?.kind === ROUTE.VIEW && VIEWS.includes(r.id)) return r.id;
  return "tasks";
};

const AppSkeleton = () => (
  <div className="shell skeleton-shell">
    <aside className="sidebar skeleton-sidebar"></aside>
    <main className="main skeleton-main">
      <div className="skeleton-title"></div>
      <div className="skeleton-box"></div>
    </main>
  </div>
);

export default function MainLayout({ user }) {
  const { logout, passwordStatus, capabilities } = useAuth();
  const { showToast } = useToast();
  const markNotificationRead = useMarkNotificationReadMutation();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const { data: assignmentApprovals = [] } = useAssignmentApprovalsQuery();

  const { data: employeesData, isLoading: isEmployeesLoading } =
    useEmployeesQuery();
  const { isLoading: isDepartmentsLoading } = useDepartmentsQuery();

  const employees = useMemo(() => employeesData ?? [], [employeesData]);

  const currentUser = useMemo(
    () => employees.find((e) => e.id === user.id) || user,
    [employees, user],
  );

  const isAppLoading = isEmployeesLoading || isDepartmentsLoading;

  const [view, setView] = useState(readInitialView);
  const [requestsTab, setRequestsTab] = useState("hours");

  const {
    modal,
    openTask,
    openProject,
    openProjectAccess,
    openChangeReq,
    openRoles,
    openDepts,
    openVacation,
    openDelegation,
    openVacNow,
    openYearCalendar,
    openEmployeeTasks,
    openCopyTask,
    openCopyProject,
    openTemplateFromTask,
    openTemplateFromProject,
    openHelp,
    openTag,
    openDelegationTasks,
    closeModal,
    setModalTab,
    openAtFile,
    openAtFolder,
  } = useModals();

  const { route, navigate: rawNavigate } = useHashRoute();

  const canAccessView = useCallback(
    (viewId) => {
      if (viewId === "journal") return capabilities.accessJournal;
      if (viewId === "library") return capabilities.accessLibrary;
      return true;
    },
    [capabilities],
  );

  const navigate = useCallback(
    (hash) => {
      rawNavigate(hash);
      const parsed = parseRoute(hash);
      if (
        parsed?.kind === ROUTE.VIEW &&
        VIEWS.includes(parsed.id) &&
        canAccessView(parsed.id)
      ) {
        queueMicrotask(() => setView(parsed.id));
      }
    },
    [rawNavigate, canAccessView],
  );

  const viewRef = useRef(view);
  const modalRef = useRef(modal);
  const routeSyncPendingRef = useRef(false);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  useEffect(() => {
    modalRef.current = modal;
  }, [modal]);

  const navItems = useMemo(
    () => [
      { id: "tasks", label: "Задачи", icon: ICONS.tasks },
      ...(capabilities.accessLibrary
        ? [
            {
              id: "library",
              label: "Шаблоны и черновики",
              icon: ICONS.bookmark,
            },
          ]
        : []),
      { id: "projects", label: "Проекты", icon: ICONS.folder },
      { id: "calendar", label: "Календарь", icon: ICONS.cal },
      { id: "gantt", label: "Диаграмма Ганта", icon: ICONS.gantt },
      { id: "staff", label: "Персонал", icon: ICONS.users },
      ...(capabilities.accessReports
        ? [{ id: "reports", label: "Отчёты", icon: ICONS.chart }]
        : []),
      { id: "archive", label: "Архив", icon: ICONS.archive },
      { id: "requests", label: "Запросы и заявки", icon: ICONS.inbox },
      ...(capabilities.accessJournal
        ? [{ id: "journal", label: "Журнал аудита", icon: ICONS.book }]
        : []),
    ],
    [capabilities],
  );

  const handleNotificationNavigate = useCallback(
    (notification) => {
      const { targetType, targetId, targetTab } = notification;
      markNotificationRead.mutate(notification.id);
      if (!targetType || !targetId) return;

      if (targetType === "changeRequest") {
        setRequestsTab(targetTab || "hours");
        navigate("#/view/requests");
        return;
      }

      if (targetType === "delegation") {
        if (targetTab === "tasks") {
          openDelegationTasks(targetId);
        } else {
          setRequestsTab("rd");
          navigate("#/view/requests");
        }
        return;
      }

      const requestsTabForType = REQUESTS_TAB_BY_NOTIF_TYPE[targetType];
      if (requestsTabForType) {
        setRequestsTab(requestsTabForType);
        navigate("#/view/requests");
        return;
      }

      switch (targetType) {
        case "task":
          openTask(targetId, targetTab || "form");
          break;
        case "project":
          openProject(targetId, targetTab || "info");
          break;
        case "vacation":
          openVacation(targetId);
      }
    },
    [markNotificationRead, openTask, openProject, openVacation, navigate],
  );

  const handleNavClick = useCallback(
    (id) => {
      if (id === "requests") {
        const hasPendingForMe = assignmentApprovals.some(
          (a) => a.status === "pending" && a.approverId === currentUser.id,
        );
        setRequestsTab(hasPendingForMe ? "assignments" : "hours");
      }
      navigate(`#/view/${id}`);
    },
    [navigate, assignmentApprovals, currentUser.id],
  );

  const desiredHash = useMemo(() => {
    if (modal?.type === "task" && modal.taskId) {
      return buildRoute({
        kind: ROUTE.TASK,
        id: modal.taskId,
        tab: modal.initialTab || DEFAULT_TAB[ROUTE.TASK],
      });
    }
    if (modal?.type === "project" && modal.projectId) {
      return buildRoute({
        kind: ROUTE.PROJECT,
        id: modal.projectId,
        tab: modal.initialTab || DEFAULT_TAB[ROUTE.PROJECT],
      });
    }
    if (modal) return "";
    return buildRoute({ kind: ROUTE.VIEW, id: view });
  }, [modal, view]);

  const denyAccess = useCallback(() => {
    queueMicrotask(() => {
      showToast("Доступ запрещён", "error");
      closeModal();
      const fallbackView = canAccessView(viewRef.current)
        ? viewRef.current
        : "tasks";
      const fallback = buildRoute({ kind: ROUTE.VIEW, id: fallbackView });
      if (fallback && window.location.hash !== fallback) navigate(fallback);
    });
  }, [showToast, closeModal, navigate, canAccessView]);

  useEffect(() => {
    if (!route || isAppLoading) return;

    if (route.kind === ROUTE.VIEW) {
      if (route.legacyId) {
        navigate(buildRoute({ kind: ROUTE.VIEW, id: route.id }));
        return;
      }
      if (!VIEWS.includes(route.id)) return;
      if (!canAccessView(route.id)) {
        denyAccess();
        return;
      }
      if (modalRef.current) {
        closeModal();
        routeSyncPendingRef.current = true;
      }
      return;
    }

    if (route.kind === ROUTE.TASK) {
      const m = modalRef.current;
      const mTab = m?.initialTab || DEFAULT_TAB[ROUTE.TASK];
      const routeTab = route.tab || DEFAULT_TAB[ROUTE.TASK];
      if (m?.type === "task" && m.taskId === route.id && mTab === routeTab)
        return;

      const fromTask = m?.type === "task" && m?.taskId === route.id ? m : null;
      routeSyncPendingRef.current = true;
      openTask(
        route.id,
        route.tab,
        fromTask?.parentTaskId ?? null,
        fromTask?.initialProjectId ?? null,
        fromTask?.returnToProjectId ?? null,
        fromTask?.returnToProjectTab ?? "info",
        fromTask?.returnToTaskId ?? null,
        fromTask?.returnToTaskTab ?? "subtasks",
        fromTask?.returnToEmployeeTasksId ?? null,
        fromTask?.returnToEmployeeTasksRange ?? null,
        fromTask?.returnToTag ?? null,
      );
      return;
    }

    if (route.kind === ROUTE.PROJECT) {
      const m = modalRef.current;
      const mTab = m?.initialTab || DEFAULT_TAB[ROUTE.PROJECT];
      const routeTab = route.tab || DEFAULT_TAB[ROUTE.PROJECT];
      if (
        m?.type === "project" &&
        m.projectId === route.id &&
        mTab === routeTab
      )
        return;

      const fromProject =
        m?.type === "project" && m?.projectId === route.id ? m : null;
      routeSyncPendingRef.current = true;
      openProject(
        route.id,
        route.tab,
        fromProject?.returnToProjectId ?? null,
        fromProject?.returnToProjectTab ?? "info",
        fromProject?.returnToTaskId ?? null,
        fromProject?.returnToTaskTab ?? "form",
        fromProject?.returnToTag ?? null,
      );
      return;
    }

    if (route.kind === ROUTE.FILE) {
      routeSyncPendingRef.current = true;
      openAtFile("task", route.id, route.id);
      return;
    }

    if (route.kind === ROUTE.FOLDER) {
      routeSyncPendingRef.current = true;
      openAtFolder("task", route.id, route.id);
      return;
    }
  }, [
    route,
    isAppLoading,
    navigate,
    canAccessView,
    denyAccess,
    closeModal,
    openTask,
    openProject,
    openAtFile,
    openAtFolder,
  ]);

  useEffect(() => {
    if (isAppLoading) return;
    if (routeSyncPendingRef.current) {
      routeSyncPendingRef.current = false;
      return;
    }
    if (desiredHash && window.location.hash !== desiredHash)
      navigate(desiredHash);
  }, [desiredHash, navigate, isAppLoading]);

  if (isAppLoading) return <AppSkeleton />;

  const renderView = () => {
    const common = { ur: currentUser, openTask, openProject };
    switch (view) {
      case "tasks":
        return (
          <TasksView
            {...common}
            openCopyTask={openCopyTask}
            openTemplateFromTask={openTemplateFromTask}
            openTag={openTag}
          />
        );
      case "library":
        return (
          <LibraryView
            ur={currentUser}
            openTask={openTask}
            openProject={openProject}
          />
        );
      case "gantt":
        return <Gantt {...common} />;
      case "calendar":
        return (
          <Calendar
            {...common}
            openCopyTask={openCopyTask}
            openTemplateFromTask={openTemplateFromTask}
          />
        );
      case "projects":
        return (
          <ProjectsView
            {...common}
            openCopyProject={openCopyProject}
            openTemplateFromProject={openTemplateFromProject}
            openProjectAccess={openProjectAccess}
            openTag={openTag}
          />
        );
      case "cabinet":
        return (
          <CabinetView
            user={currentUser}
            openTask={openTask}
            openProject={openProject}
            openVacation={openVacation}
            openDelegation={openDelegation}
          />
        );
      case "staff":
        return (
          <StaffView
            ur={currentUser}
            openRoles={openRoles}
            openDepts={openDepts}
            openVacation={openVacation}
            openEmployeeTasks={openEmployeeTasks}
          />
        );
      case "reports":
        return <ReportsView ur={currentUser} />;
      case "archive":
        return (
          <ArchiveView
            ur={currentUser}
            openTask={openTask}
            openProject={openProject}
          />
        );
      case "requests":
        return <RequestsView initialTab={requestsTab} key={requestsTab} />;
      case "journal":
        return <JournalView />;
      default:
        return null;
    }
  };

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="logo">
          <div className="logo-mark">АГ</div>
          <div>
            <div className="logo-name">АвиаГоризонт</div>
            <div className="logo-sub">планирование и учёт времени</div>
          </div>
        </div>

        <nav className="nav">
          {navItems.map((n) => (
            <button
              key={n.id}
              className={`nav-item${view === n.id ? " on" : ""}`}
              onClick={() => handleNavClick(n.id)}
            >
              <Ic d={n.icon} /> <span className="nav-lbl">{n.label}</span>
            </button>
          ))}
        </nav>

        <div className="side-foot">
          <div
            className="user-card cursor-pointer"
            onClick={() => navigate("#/view/cabinet")}
          >
            <div className="avatar">
              {currentUser.photo ? (
                <img
                  src={currentUser.photo}
                  alt="Аватар"
                  className="user-card-photo"
                />
              ) : (
                initials(currentUser.first, currentUser.last)
              )}
            </div>
            <div className="user-meta">
              <div className="user-name">
                {currentUser.first} {currentUser.last}
              </div>
              <div className="user-roles">{currentUser.roles.join(" · ")}</div>
              <SubstitutionBadge
                user={currentUser}
                empName={(id) => employees.find((e) => e.id === id)?.last || id}
              />
            </div>
            <button
              className="icon-btn dark"
              onClick={(e) => {
                e.stopPropagation();
                logout();
              }}
              title="Выйти"
            >
              <Ic d={ICONS.out} size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        {passwordStatus?.needsReminder && (
          <div className="password-warning-banner">
            <span>
              <strong>Внимание:</strong> Ваш пароль истекает через{" "}
              {passwordStatus.daysLeft} дн. Пожалуйста, смените его для
              безопасности.
            </span>
            <button
              className="btn ghost sm"
              onClick={() => setIsPasswordModalOpen(true)}
            >
              Сменить пароль
            </button>
          </div>
        )}

        <header className="topbar">
          <div>
            <h1 className="page-title">
              {navItems.find((n) => n.id === view)?.label || "Личный кабинет"}
            </h1>
            <div className="page-sub">
              {new Date().toLocaleDateString("ru-RU", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
          <div className="top-tools">
            <button className="btn ghost" onClick={openVacNow}>
              <Ic d={ICONS.beach} size={15} /> Сотрудники в отпусках
            </button>
            <button className="btn ghost" onClick={openYearCalendar}>
              <Ic d={ICONS.cal} size={15} /> Производственный календарь
            </button>
            <ThemeToggle />
            <NotifBell
              user={currentUser}
              onNavigate={handleNotificationNavigate}
            />
            <button
              className="icon-btn"
              onClick={openHelp}
              title="Справка"
              aria-label="Открыть справку"
            >
              <Ic d={ICONS.help} size={17} /> Справка
            </button>
          </div>
        </header>

        <div className="content">{renderView()}</div>
      </main>

      {modal && (
        <ModalRenderer
          key={modal._seq}
          modal={modal}
          onClose={closeModal}
          ur={currentUser}
          openTask={openTask}
          openProject={openProject}
          openChangeReq={openChangeReq}
          openRoles={openRoles}
          openDepts={openDepts}
          openVacation={openVacation}
          openDelegation={openDelegation}
          openEmployeeTasks={openEmployeeTasks}
          openCopyTask={openCopyTask}
          openCopyProject={openCopyProject}
          openTag={openTag}
          onTabChange={setModalTab}
          toast={showToast}
        />
      )}

      {isPasswordModalOpen && (
        <ChangePasswordModal onClose={() => setIsPasswordModalOpen(false)} />
      )}
    </div>
  );
}
